const CACHE_PREFIX = "standardReportsCache:v1:";
const CACHE_INDEX_KEY = `${CACHE_PREFIX}__index__`;
const CACHE_NONCE_KEY = `${CACHE_PREFIX}__nonce__`;
const MAX_ENTRIES = 250;

const memory = new Map();
let localNonce = null;

function ensureNonceSynced() {
  if (typeof sessionStorage === "undefined") return;
  try {
    const sessionNonce = sessionStorage.getItem(CACHE_NONCE_KEY);
    if (!sessionNonce) {
      const newNonce = `${Date.now()}_${Math.random()}`;
      sessionStorage.setItem(CACHE_NONCE_KEY, newNonce);
      localNonce = newNonce;
      memory.clear();
      return;
    }
    if (localNonce && sessionNonce !== localNonce) {
      memory.clear();
    }
    localNonce = sessionNonce;
  } catch {}
}

function stableStringify(value) {
  if (value == null) return String(value);
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
}

function makeKey(parts) {
  const arr = Array.isArray(parts) ? parts : [parts];
  return arr.map(stableStringify).join("|");
}

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readIndex() {
  try {
    const raw = sessionStorage.getItem(CACHE_INDEX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(keys) {
  try {
    sessionStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(keys));
  } catch {}
}

function pruneIfNeeded() {
  if (memory.size <= MAX_ENTRIES) return;
  const keys = readIndex();
  if (!keys.length) return;
  const overflow = Math.max(0, keys.length - MAX_ENTRIES);
  if (!overflow) return;
  const toRemove = keys.slice(0, overflow);
  const nextKeys = keys.slice(overflow);
  toRemove.forEach((k) => {
    memory.delete(k);
    try {
      sessionStorage.removeItem(`${CACHE_PREFIX}${k}`);
    } catch {}
  });
  writeIndex(nextKeys);
}

function writeSession(key, value) {
  ensureNonceSynced();
  try {
    sessionStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(value));
  } catch {}
  const idx = readIndex();
  if (!idx.includes(key)) {
    idx.push(key);
    writeIndex(idx);
  }
  pruneIfNeeded();
}

export async function standardReportsCached(parts, fetcher) {
  ensureNonceSynced();
  const key = makeKey(parts);
  const entry = memory.get(key);
  if (entry?.status === "resolved") return entry.value;
  if (entry?.status === "pending") return entry.promise;

  const cachedValue = typeof sessionStorage !== "undefined" ? readSession(key) : null;
  if (cachedValue != null) {
    memory.set(key, { status: "resolved", value: cachedValue });
    return cachedValue;
  }

  const controller = new AbortController();
  const promise = Promise.resolve()
    .then(() => fetcher({ signal: controller.signal }))
    .then((value) => {
      if (controller.signal.aborted) {
        memory.delete(key);
        return value;
      }
      memory.set(key, { status: "resolved", value });
      if (typeof sessionStorage !== "undefined") writeSession(key, value);
      return value;
    })
    .catch((err) => {
      memory.delete(key);
      throw err;
    });

  memory.set(key, { status: "pending", promise, controller });
  pruneIfNeeded();
  return promise;
}

export function abortStandardReportsInFlight() {
  for (const [key, entry] of memory.entries()) {
    if (entry?.status === "pending" && entry?.controller) {
      try {
        entry.controller.abort();
      } catch {}
      memory.delete(key);
    }
  }
}

export function clearStandardReportsCache() {
  abortStandardReportsInFlight();
  memory.clear();
  try {
    const keys = readIndex();
    keys.forEach((k) => sessionStorage.removeItem(`${CACHE_PREFIX}${k}`));
    sessionStorage.removeItem(CACHE_INDEX_KEY);
    sessionStorage.removeItem(CACHE_NONCE_KEY);
  } catch {}
}

function removeKeysFromCache(keysToRemove) {
  const set = new Set(Array.isArray(keysToRemove) ? keysToRemove : []);
  if (!set.size) return;

  for (const [key, entry] of memory.entries()) {
    if (!set.has(key)) continue;
    if (entry?.status === "pending" && entry?.controller) {
      try {
        entry.controller.abort();
      } catch {}
    }
    memory.delete(key);
  }

  try {
    set.forEach((k) => sessionStorage.removeItem(`${CACHE_PREFIX}${k}`));
    const idx = readIndex();
    const next = idx.filter((k) => !set.has(k));
    writeIndex(next);
  } catch {}
}

export function clearStandardReportsCacheForSync({
  syncId,
  erp,
  externalId,
  types,
} = {}) {
  const typeList = Array.isArray(types) && types.length ? types : [];
  if (!typeList.length) return;

  const prefixes = [];
  typeList.forEach((t) => {
    const typeKey = String(t || "").trim();
    if (!typeKey) return;
    if (typeKey.endsWith("Config") && syncId) {
      prefixes.push(`${makeKey([typeKey, { syncId }])}|`);
      prefixes.push(makeKey([typeKey, { syncId }]));
      return;
    }
    if (erp != null && externalId) {
      prefixes.push(`${makeKey([typeKey, { erp, externalId }])}|`);
      prefixes.push(makeKey([typeKey, { erp, externalId }]));
    }
  });

  if (!prefixes.length) return;

  const idx = readIndex();
  const toRemove = idx.filter((k) => prefixes.some((p) => String(k).startsWith(p)));
  removeKeysFromCache(toRemove);
}
